import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'

export default function MonogramInput({ prependInputNames = true, ...props }) {
  const [noMonogram, setNoMonogram] = useState(false)
  const [initial1, setInitial1] = useState(props.initial1 || '')
  const [initial2, setInitial2] = useState(props.initial2 || '')
  const [initial3, setInitial3] = useState(props.initial3 || '')
  const [inError, setInError] = useState(false)

  useEffect(() => {
    if (props.addToCart) {
      props.addToCart.addEventListener('click', onFormSubmit)
      // Return cleanup code
      return () => {
        props.addToCart.removeEventListener('click', onFormSubmit)
      }
    }
  }, [props.addToCart])

  useEffect(() => {
    // If the user is turning on the "No monogram" option, immediately remove the error, since it's no longer in error at that point.
    // Remove any monogram letters entered as well.
    if (noMonogram) {
      setInError(false)
      setInitial1('')
      setInitial2('')
      setInitial3('')
    }
  }, [noMonogram])

  useEffect(() => {
    if (initial1.length + initial2.length + initial3.length > 0) {
      setNoMonogram(false)
    }
  }, [initial1, initial2, initial3])

  const onFormSubmit = (e) => {
    // The values in this class aren't accessible from the form event, so we have to pull them manually from the DOM
    const formElements = e.currentTarget.form.elements
    const noMonogram = formElements.no_monogram.checked
    const initials = ['monogram_first', 'monogram_middle', 'monogram_last'].map((id) => formElements[id].value)
    const error = !noMonogram && initials.every((val) => /^[^A-Z]?$/.test(val))
    setInError(error)

    if (error) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
  }

  const toggleNoMonogram = () => {
    setNoMonogram((value) => !value)
  }

  const initialUpdate = (setFunction) => {
    return function(e) {
      const value = e.target.value.toUpperCase()
      setFunction(value)
      if (value.length === 1) {
        if (/[A-Z]/.test(value)) {
          e.target.setCustomValidity('')
          focusNext(e.target, 1)
        } else {
          e.target.setCustomValidity('Alphabetical letters only')
        }
        e.target.reportValidity()
      }
    }
  }

  const goback = (e) => {
    if (e.keyCode === 8 && e.target.value.length === 0) {
      focusNext(e.target, -1)
    }
  }

  const focusNext = (origin, addition) => {
    const orderedInputs = props.monogramStraight
      ? ['monogram_first', 'monogram_middle', 'monogram_last']
      : ['monogram_first', 'monogram_last', 'monogram_middle']
    let index = orderedInputs.indexOf(origin.id)
    if (index === -1) return
    index += addition
    if (index < 0 || index > 2) return

    document.getElementById(orderedInputs[index]).focus()
  }

  const inputName = (val) => {
    return prependInputNames ? `properties[${val}]` : val
  }

  return (
    <div class={`monogram-input ${inError && 'monogram-input-error'}`}>
      <div class={`input initials ${props.monogramStraight && ' monogram-straight'}`}>
        <div class="initial initial-first">
          <label for="monogram_first">First Name<em>Initial</em></label>
          <input id="monogram_first"
            name={inputName('First Name Initial')}
            tabindex="1"
            type="text"
            size="1"
            maxlength="1"
            pattern="[A-z]?"
            title="Alphabetical letters only"
            autocapitalize="characters"
            autocomplete="off"
            value={initial1}
            onInput={initialUpdate(setInitial1)}
            onKeyDown={goback}
          />
        </div>
        <div class="initial initial-last">
          <label for="monogram_last">Last Name<em>Initial</em></label>
          <input id="monogram_last"
            name={inputName('Last Name Initial')}
            tabindex="2"
            type="text"
            size="1"
            maxlength="1"
            pattern="[A-z]?"
            title="Alphabetical letters only"
            autocapitalize="characters"
            autocomplete="off"
            value={initial2}
            onInput={initialUpdate(setInitial2)}
            onKeyDown={goback}
          />
        </div>
        <div class="initial initial-middle">
          <label for="monogram_middle">Middle Name<em>Initial</em></label>
          <input id="monogram_middle"
            name={inputName('Middle Name Initial')}
            tabindex="2"
            type="text"
            size="1"
            maxlength="1"
            pattern="[A-z]?"
            title="Alphabetical letters only"
            autocapitalize="characters"
            autocomplete="off"
            value={initial3}
            onInput={initialUpdate(setInitial3)}
            onKeyDown={goback}
          />
        </div>
      </div>

      {props.tagline && <p class="tagline">{props.tagline}</p>}

      <p class="product-monogram-checkbox line-item-property__field">
        <input type="hidden" name={inputName('_Monogram')} value="Yes" />
        <label>
          <input id="no_monogram" type="checkbox" name={inputName('_Monogram')} value="No" checked={noMonogram} onChange={toggleNoMonogram} />
          No Monogram (I Want ASAP)
        </label>
      </p>

      {inError &&
        <div class="fill-out-monogram-message">
          Please enter a monogram with at least one letter.<br />
          If you do not want a monogram, check <span class="important-no-wrap">No Monogram</span> above.
        </div>
      }

    </div>
  )
}
